import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { DataTable } from "../shared/DataTable";
import { Pagination } from "../shared/Pagination";
import { StatusBadge } from "../shared/StatusBadge";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { Input } from "../ui/input";
import { PlanFormDialog } from "./PlanFormDialog";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { itemStatusMeta } from "../../lib/statusMaps";
import { formatCurrency } from "../../lib/utils";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useListState } from "../../hooks/useListState";

const LIMIT = 20;

export function PlansTab() {
  const [state, setState] = useState({ loading: true, rows: [], total: 0 });
  const [page, setPage] = useListState("catalog.plans.page", 0);
  const [search, setSearch] = useListState("catalog.plans.search", "");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [dialogPlan, setDialogPlan] = useState(undefined);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const refresh = () => {
    setState((s) => ({ ...s, loading: true }));
    apiClient.get("/plans", { params: { q: debouncedSearch || undefined, limit: LIMIT, offset: page * LIMIT } })
      .then(({ data }) => setState({ loading: false, rows: data.items, total: data.total }))
      .catch((error) => { toast.error(getApiErrorMessage(error, "Could not load plans.")); setState((s) => ({ ...s, loading: false })); });
  };

  useEffect(refresh, [page, debouncedSearch]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await apiClient.delete(`/plans/${deleteTarget.id}`);
      toast.success("Plan deleted");
      setDeleteTarget(null);
      refresh();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not delete plan."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-4" data-testid="plans-tab">
      <div className="mb-4 flex items-center justify-between gap-3">
        <Input
          placeholder="Search plans..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          className="max-w-xs"
          data-testid="plans-search-input"
        />
        <Button size="sm" onClick={() => setDialogPlan(null)} data-testid="plans-new-button">
          <Plus size={15} strokeWidth={1.5} /> New Plan
        </Button>
      </div>

      <DataTable
        testId="plans-table"
        loading={state.loading}
        rows={state.rows}
        emptyTitle="No plans yet"
        columns={[
          { key: "name", header: "Name" },
          { key: "price", header: "Price", render: (r) => formatCurrency(r.price) },
          { key: "quantity", header: "Total Days", render: (r) => (r.quantity != null ? `${r.quantity} days` : "—") },
          { key: "status", header: "Status", render: (r) => { const m = itemStatusMeta(r.status); return <StatusBadge label={m.label} variant={m.variant} />; } },
          {
            key: "actions", header: "", render: (r) => (
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="icon" onClick={() => setDialogPlan(r)} data-testid={`plans-edit-${r.id}`}>
                  <Pencil size={14} strokeWidth={1.5} />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setDeleteTarget(r)} data-testid={`plans-delete-${r.id}`}>
                  <Trash2 size={14} strokeWidth={1.5} className="text-danger" />
                </Button>
              </div>
            ),
          },
        ]}
      />
      <Pagination page={page} onPageChange={setPage} total={state.total} pageSize={LIMIT} testId="plans-pagination" />

      <PlanFormDialog
        open={dialogPlan !== undefined} onOpenChange={(v) => !v && setDialogPlan(undefined)}
        plan={dialogPlan} onSaved={() => { setDialogPlan(undefined); refresh(); }}
      />
      <ConfirmDialog
        open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete this plan?" description={`"${deleteTarget?.name}" will be marked deleted. Existing subscriptions on it are unaffected.`}
        confirmLabel="Delete Plan" variant="danger" loading={deleting} onConfirm={handleDelete}
        testId="plans-delete-confirm-dialog"
      />
    </div>
  );
}
